/* istanbul ignore file */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import get from 'lodash/get';
import kebabCase from 'lodash/kebabCase';
import classNames from 'classnames';
import { trackSearchEvent } from '@atlassian/temp-analytics';
import { Checkbox } from '@atlaskit/checkbox';
import Button from '@atlaskit/button';
import { transformFilterableFields } from '@atlassian/cssxp-utils-generic';
import {
  emptyStateProp as defaultEmptyStateProp,
  errorStateProp,
  getSelectedColumns,
  TableHeading,
  LimitCurrencyDropdown,
} from '@css-xp/ppc-common';
import {
  emptyStatePropForSubscriptionTableFlat,
  emptyStatePropForWrongSearch,
} from '@css-xp/ppc-common/src/state-props';
import {
  ColGroup,
  CustomizeView,
  CustomSearch,
  HeadCell,
  SearchResultColumn,
  TableBody,
  TableHead,
  TabPanel,
  Tabs,
  SortableTableHead,
  useFeatureToggle,
  CustomFilterCheckboxWrapper,
  DateFilterWithBadge,
  DateRangeType,
} from '@css-xp/base';
import { CustomDynamicResultTable } from '@css-xp/base/custom-dynamic-result-table';
import { generatedTypesNew } from '@css-xp/graphtypes';
import { FilterOption, SortType, SortDirection, ContactInfo } from '@css-xp/graphtypes/src/generated-types-new';
import {
  DEFAULT_PAGE,
  DEFAULT_PAGE_SIZE,
  SUBSCRIPTION_CONSTANTS,
  ENABLE_DESIGN_ITERATION_1,
  STATSIG_FEATURE_GATES,
  maxThresholdForResults,
} from '@css-xp/ppc-constants';
import {
  isCCPEntitlement,
  isURLAddress,
  getSortDirectionMatchFromSortOptions,
  getFormattedDateFromTimeStamp,
} from '@css-xp/ppc-utils';
import { isEntitlementOnGracePeriod } from '@css-xp/ppc-utils/src/entitlement-utils';
import { SubscriptionTableRow } from './subscription-table-row';
import { DownloadCSVPerfV2 } from './components';
import { TransferSubscriptionsInBulk } from './components/bulk-transfer-subscriptions';
// @ts-ignore
import MoveBulkIcon from '@css-xp/base/assets/img/icon-move-bulk.svg';
import './subscription-table.scss';

interface SubscriptionTableFlatProps {
  transactionAccountId?: string;
  label?: string;
  shouldHideDownloadCsv?: boolean | null;
  dataState: {
    data: generatedTypesNew.ProductAppSubscription[];
    loading: boolean;
    refetch: (
      _variables?:
        | Partial<{
            searchOptions: generatedTypesNew.ConnectionSearchOptions;
            filterOptions: generatedTypesNew.FilterOption[];
          }>
        | undefined,
      _showApps?: boolean
    ) => void;
    error: string | undefined;
    totalCount: number;
  };
  testId?: string;
  isPaginationEnabled?: boolean;
  isFreeTabEnabled?: boolean;
  downloadCSVFilter?: FilterOption[];
  defaultSortOptions?: SortType;
  shouldHideCustomiseView?: boolean;
  shouldHideTabs?: boolean;
  isRowSelectionRequired?: boolean;
  onRowSelected?: (_p: string) => void;
  onAllRowSelected?: () => void;
  areAllRowsSelected?: boolean;
  selectedSubscriptionIds?: string[];
  isBulkTransferButtonRequired?: boolean;
  resetRowSelections?: () => void;
  columnsToShow?: string[];
  shouldAllowBillingProfileTransferBasedOnPricingEligibility?: boolean;
  allowedPricingEligibilityForBillingProfileTransfer?: string[];
  isFutureDatedSubscriptionsEnabled?: boolean;
  eccDetails: { [key: string]: Array<ContactInfo> };
  relatedDomains: string[];
  isInternalUsageLicensesEnabled?: boolean;
  isTWCPhase1Enabled?: boolean;
  shouldFetchInternalLicenses?: boolean;
}

interface SorterColumnHeadProps {
  rowsInPage: number;
  isAppsInSearch: boolean;
}

type ColumnList = HeadCell & SearchResultColumn;

interface FormData {
  searchTerm: string;
}

export const DefaultIsRenewOnAscSorted = true;
export const DefaultIsSiteAscSorted = true;
export const DefaultShowAppsInSearch = false;

export const dateRangeKeys = {
  renewalDate: 'lastPeriodEndDate', // RENEW_DATE
};

const buildSearchFilters = (searchInput: string) => {
  searchInput = searchInput.trim();

  if (isURLAddress(searchInput)) {
    return [{ cloudSiteHostname: searchInput }];
  }

  if (isCCPEntitlement(searchInput)) {
    return [{ entitlementNumber: searchInput }];
  }

  return [
    { productNamePartial: searchInput },
    { cloudSiteHostname: searchInput },
    { edition: searchInput },
    { entitlementNumber: searchInput },
    { id: searchInput },
    { soldToAddressPartial: searchInput },
    { organization: searchInput },
  ];
};

export const getFilterOptions = (searchInput?: string, selectedTab?: string, defaultStatuses?: string[]) => {
  const filterOptions: FilterOption[] = [];
  const tabToFilterKeyMap: any = {
    [SUBSCRIPTION_CONSTANTS.TAB_ENUM.ACTIVE]: 'status',
    [SUBSCRIPTION_CONSTANTS.TAB_ENUM.INACTIVE]: 'status',
    [SUBSCRIPTION_CONSTANTS.TAB_ENUM.FREE]: 'edition',
  };
  if (searchInput) {
    filterOptions.push({
      key: 'or',
      value: JSON.stringify(buildSearchFilters(searchInput)),
    });
  }
  if (selectedTab && selectedTab !== SUBSCRIPTION_CONSTANTS.TAB_ENUM.UPCOMING) {
    filterOptions.push({
      key: tabToFilterKeyMap[selectedTab || SUBSCRIPTION_CONSTANTS.TAB_ENUM.ACTIVE],
      value: selectedTab || SUBSCRIPTION_CONSTANTS.TAB_ENUM.ACTIVE,
    });
  }

  if (selectedTab && selectedTab === SUBSCRIPTION_CONSTANTS.TAB_ENUM.UPCOMING) {
    filterOptions.push({
      key: 'or',
      value: JSON.stringify([
        {
          fetchUpcomingSubscriptions: true,
        },
      ]),
    });
  }

  if (defaultStatuses && defaultStatuses.length > 0) {
    const statusFilter: any = [];
    defaultStatuses.forEach((status) => {
      statusFilter.push({ status });
    });
    filterOptions.push({
      key: 'and',
      value: JSON.stringify([{ or: statusFilter }]),
    });
  }

  return filterOptions;
};

const getSiteSortType = (isSiteAscSorted?: boolean): SortType => {
  return {
    sortFields: [SUBSCRIPTION_CONSTANTS.SORT_KEYS.CLOUD_SITE, SUBSCRIPTION_CONSTANTS.SORT_KEYS.ID],
    sortFieldsDirections: [isSiteAscSorted ? SortDirection.ASC : SortDirection.DESC, SortDirection.DESC],
  };
};

export const getRenewOnSortType = (isRenewOnAscSorted?: boolean): SortType => {
  return {
    sortFields: [SUBSCRIPTION_CONSTANTS.SORT_KEYS.RENEW_DATE, SUBSCRIPTION_CONSTANTS.SORT_KEYS.ID],
    sortFieldsDirections: [isRenewOnAscSorted ? SortDirection.ASC : SortDirection.DESC, SortDirection.DESC],
  };
};

const getCreatedSortType = (isCreatedDescSorted?: boolean): SortType => {
  return {
    sortFields: [SUBSCRIPTION_CONSTANTS.SORT_KEYS.CREATED_DATE, SUBSCRIPTION_CONSTANTS.SORT_KEYS.ID],
    sortFieldsDirections: [isCreatedDescSorted ? SortDirection.DESC : SortDirection.ASC, SortDirection.DESC],
  };
};

const getPlanSortType = (isSiteAscSorted?: boolean): SortType => {
  return {
    sortFields: [SUBSCRIPTION_CONSTANTS.SORT_KEYS.PLAN, SUBSCRIPTION_CONSTANTS.SORT_KEYS.ID],
    sortFieldsDirections: [isSiteAscSorted ? SortDirection.ASC : SortDirection.DESC, SortDirection.DESC],
  };
};

const defaultColumns = [
  SUBSCRIPTION_CONSTANTS.COLUMNS.SUBSCRIPTION,
  SUBSCRIPTION_CONSTANTS.COLUMNS.STATUS,
  SUBSCRIPTION_CONSTANTS.COLUMNS.OFFERING,
  SUBSCRIPTION_CONSTANTS.COLUMNS.ADD_ONS,
  SUBSCRIPTION_CONSTANTS.COLUMNS.APPS,
  SUBSCRIPTION_CONSTANTS.COLUMNS.COMPANY,
  SUBSCRIPTION_CONSTANTS.COLUMNS.ORGANIZATION_NAME,
  SUBSCRIPTION_CONSTANTS.COLUMNS.SITE,
  SUBSCRIPTION_CONSTANTS.COLUMNS.USAGE,
  SUBSCRIPTION_CONSTANTS.COLUMNS.BILLING_PROFILE,
  SUBSCRIPTION_CONSTANTS.COLUMNS.CREATED,
  SUBSCRIPTION_CONSTANTS.COLUMNS.RENEW_ON,
  SUBSCRIPTION_CONSTANTS.COLUMNS.LAST_INVOICE_AMOUNT,
];

enum SubscriptionStatusType {
  ACTIVE = 'Active',
  INACTIVE = 'Inactive',
  UPCOMING = 'Upcoming',
}

enum SubscriptionPlanType {
  FREE = 'Free',
  STANDARD = 'Standard',
  PREMIUM = 'Premium',
  ENTERPRISE = 'Enterprise',
  STARTER = 'Starter',
  BUSINESS = 'Business',
  BUSINESS_AI = 'Business + Ai',
}

enum SubscriptionStatusFilterOptions {
  ACTIVE = 'Active',
  INACTIVE = 'Inactive',
  UPCOMING = 'Upcoming',
}

enum SubscriptionPlanFilterOptions {
  FREE = 'Free',
  STANDARD = 'Standard',
  PREMIUM = 'Premium',
  ENTERPRISE = 'Enterprise',
  STARTER = 'Starter',
  BUSINESS = 'Business',
}

const BUSINESS_AI_KEY = 'Business + AI'; // As transformFilterableFields does not handle + sign, so adding it manually
const initialSelectedFieldsForStatus: string[] = [];

const initialPlanFilterFields = [
  {
    filterItem: 'Plan',
    queryKey: 'edition',
    iterableValues: [
      ...transformFilterableFields(SubscriptionPlanFilterOptions),
      {
        // As transformFilterableFields does not handle + sign, so adding it manually
        key: BUSINESS_AI_KEY,
        value: SubscriptionPlanType.BUSINESS_AI,
      },
    ],
    defaultSelectedFields: initialSelectedFieldsForStatus,
  },
];

const statusFilterMap: Record<string, string> = {
  [SubscriptionStatusFilterOptions.ACTIVE]: SubscriptionStatusType.ACTIVE,
  [SubscriptionStatusFilterOptions.INACTIVE]: SubscriptionStatusType.INACTIVE,
  [SubscriptionStatusFilterOptions.UPCOMING]: SubscriptionStatusType.UPCOMING,
};

const planFilterMap: Record<string, string> = {
  [SubscriptionPlanFilterOptions.FREE]: SubscriptionPlanType.FREE,
  [SubscriptionPlanFilterOptions.STANDARD]: SubscriptionPlanType.STANDARD,
  [SubscriptionPlanFilterOptions.PREMIUM]: SubscriptionPlanType.PREMIUM,
  [SubscriptionPlanFilterOptions.ENTERPRISE]: SubscriptionPlanType.ENTERPRISE,
  [SubscriptionPlanFilterOptions.STARTER]: SubscriptionPlanType.STARTER,
  [SubscriptionPlanFilterOptions.BUSINESS]: SubscriptionPlanType.BUSINESS,
  [SubscriptionPlanType.BUSINESS_AI]: SubscriptionPlanType.BUSINESS_AI,
};

const renewalDateRangeOption = [
  { text: 'Next 24 hours', value: -1 },
  { text: 'Next 7 days', value: -7 },
  { text: 'Next 14 days', value: -14 },
  { text: 'Next 30 days', value: -30 },
  { text: 'Next 90 days', value: -90 },
  { text: 'Next 12 months', value: -365 },
  { text: 'Custom', value: 0 },
];

export const SubscriptionTableFlat = ({
  transactionAccountId,
  label,
  shouldHideDownloadCsv = false,
  dataState,
  testId,
  isPaginationEnabled,
  isFreeTabEnabled,
  downloadCSVFilter,
  defaultSortOptions = getSiteSortType(DefaultIsSiteAscSorted),
  shouldHideCustomiseView = false,
  shouldHideTabs = false,
  isRowSelectionRequired = false,
  onRowSelected,
  onAllRowSelected,
  areAllRowsSelected = false,
  selectedSubscriptionIds,
  isBulkTransferButtonRequired = false,
  resetRowSelections,
  columnsToShow = defaultColumns,
  shouldAllowBillingProfileTransferBasedOnPricingEligibility = false,
  allowedPricingEligibilityForBillingProfileTransfer = [],
  isFutureDatedSubscriptionsEnabled = false,
  eccDetails,
  relatedDomains,
  isInternalUsageLicensesEnabled = false,
  isTWCPhase1Enabled = false,
  shouldFetchInternalLicenses,
}: SubscriptionTableFlatProps) => {
  const [isEnabled] = useFeatureToggle();
  const isDesignIteration1Enabled = isEnabled(ENABLE_DESIGN_ITERATION_1);
  const isDefaultFiltersEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_DEFAULT_FILTERS);
  const isRedesignFastFollowEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_REDESIGN_FAST_FOLLOW);

  const [initialSelectedFieldsForStatusFilter, setInitialSelectedFieldsForStatusFilter] = useState<string[]>(
    isFutureDatedSubscriptionsEnabled
      ? [SubscriptionStatusType.ACTIVE, SubscriptionStatusType.UPCOMING]
      : [SubscriptionStatusType.ACTIVE]
  );
  const statusFilterCommonFields = [
    // Common value which is leveraged in clear all filter function for clearing status filter state
    {
      filterItem: 'Status',
      queryKey: 'status',
      iterableValues: isFutureDatedSubscriptionsEnabled
        ? transformFilterableFields(SubscriptionStatusFilterOptions)
        : transformFilterableFields(SubscriptionStatusFilterOptions).filter(
            (field) => field.value !== SubscriptionStatusType.UPCOMING
          ),
      defaultSelectedFields: initialSelectedFieldsForStatus,
    },
  ];
  const initialStatusFilterFields =
    isDesignIteration1Enabled && isDefaultFiltersEnabled
      ? [
          {
            ...statusFilterCommonFields[0],
            // isDesignIteration1Enabled field value to show a selected filter UI
            defaultSelectedFields: initialSelectedFieldsForStatusFilter,
          },
        ]
      : statusFilterCommonFields;
  const { data, loading, refetch, error, totalCount } = dataState;
  const [rowsPerPage, setRowsPerPage] = useState<number>(DEFAULT_PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState<number>(DEFAULT_PAGE);
  const filterOptions = useRef<FilterOption[]>(
    shouldHideTabs
      ? isDefaultFiltersEnabled
        ? getFilterOptions('', '', [SubscriptionStatusType.ACTIVE, SubscriptionStatusType.UPCOMING])
        : getFilterOptions()
      : getFilterOptions('', SUBSCRIPTION_CONSTANTS.TAB_ENUM.ACTIVE)
  );
  const [isStatusFilterAppliedByDefault, setIsStatusFilterAppliedByDefault] = useState<boolean>(
    isDefaultFiltersEnabled
  );
  const [isStatusFilterApplied, setIsStatusFilterApplied] = useState<boolean>(false);
  const [isRenewDateFilterVisible, setIsRenewDateFilterVisible] = useState<boolean>(true);
  const [isPlanFilterApplied, setIsPlanFilterApplied] = useState<boolean>(false);
  const [isRenewalFilterApplied, setIsRenewalFilterApplied] = useState<boolean>(false);
  const [searchInputText, setSearchInputText] = useState<string>('');
  const [statusFilterResetCount, setStatusFilterResetCount] = useState<number>(0);
  const [planFilterResetCount, setPlanFilterResetCount] = useState<number>(0);
  const [statusFilterFields, setStatusFilterFields] = useState(initialStatusFilterFields);
  const [planFilterFields, setPlanFilterFields] = useState(initialPlanFilterFields);
  const [statusFilterOptions, setStatusFilterOptions] = useState<Record<string, string>[]>([
    { status: SubscriptionStatusType.ACTIVE },
    ...(isDefaultFiltersEnabled ? [] : [{ status: SubscriptionStatusType.INACTIVE }]),
    ...(isFutureDatedSubscriptionsEnabled ? [{ status: SubscriptionStatusType.UPCOMING }] : []),
  ]);
  const [planFilterOptions, setPlanFilterOptions] = useState<Record<string, string>[]>([]);
  const [renewalDateFilterOptions, setRenewalDateFilterOptions] = useState<{ key: string; value: string }[]>([]);
  const [selectedRenewalDateRangeValue, setSelectedRenewalDateRangeValue] = useState('');
  const [currency, setCurrency] = useState<string>('');

  // we are setting this by default as parent container also sending this sort options initially
  const [sortOptions, setSortOptions] = useState<SortType>(defaultSortOptions);

  //Note: Using useRef because in handleSorting function isSiteAscSorted and isRenewOnAscSorted state was not changing.
  const isSiteAscSortedRef = useRef<boolean | undefined>(
    getSortDirectionMatchFromSortOptions(
      defaultSortOptions,
      SUBSCRIPTION_CONSTANTS.SORT_KEYS.CLOUD_SITE,
      generatedTypesNew.SortDirection.ASC
    )
  );
  const isRenewOnAscSortedRef = useRef<boolean | undefined>(
    getSortDirectionMatchFromSortOptions(
      defaultSortOptions,
      SUBSCRIPTION_CONSTANTS.SORT_KEYS.RENEW_DATE,
      generatedTypesNew.SortDirection.ASC
    )
  );
  const isCreatedDescSortedRef = useRef<boolean | undefined>(
    getSortDirectionMatchFromSortOptions(
      defaultSortOptions,
      SUBSCRIPTION_CONSTANTS.SORT_KEYS.CREATED_DATE,
      SortDirection.DESC
    )
  );
  const isPlanAscSortedRef = useRef<boolean | undefined>(
    getSortDirectionMatchFromSortOptions(
      defaultSortOptions,
      SUBSCRIPTION_CONSTANTS.SORT_KEYS.PLAN,
      generatedTypesNew.SortDirection.ASC
    )
  );

  const [selectedTab, setSelectedTab] = useState<string | undefined>(
    shouldHideTabs ? undefined : SUBSCRIPTION_CONSTANTS.TAB_ENUM.ACTIVE
  );
  const [showAppsInSearch, setShowAppsInSearch] = useState<boolean>(DefaultShowAppsInSearch);
  const [isSearched, setIsSearched] = useState(false);
  const hasNoResults = totalCount === 0;

  const isSubscriptionRenewalDateFilterEnabled = isEnabled(
    STATSIG_FEATURE_GATES.ENABLE_SUBSCRIPTIONS_RENEWAL_DATE_FILTER
  );
  const isSandboxSupportForSubscriptionsEnabled = isEnabled(
    STATSIG_FEATURE_GATES.ENABLE_SANDBOX_SUPPORT_FOR_SUBSCRIPTIONS
  );

  const isNonCommercialPricingEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_NON_COMMERCIAL_PRICING);
  const isMultiCurrencyChangesEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_MULTI_CURRENCY_CHANGES);

  const isAddOnsEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_ADD_ONS);

  const isCurrencyFilterApplied = useMemo(() => {
    return currency !== '';
  }, [currency]);

  const isGracePeriodVisibilityEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_GRACE_PERIOD_VISIBILITY);
  const isAppificationEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_APPIFICATION);

  const makeAllSortedRefUndefinedButOne = (refNameToKeep: string) => {
    if (refNameToKeep !== SUBSCRIPTION_CONSTANTS.SORT_KEYS.CLOUD_SITE) {
      isSiteAscSortedRef.current = undefined;
    }
    if (refNameToKeep !== SUBSCRIPTION_CONSTANTS.SORT_KEYS.RENEW_DATE) {
      isRenewOnAscSortedRef.current = undefined;
    }
    if (refNameToKeep !== SUBSCRIPTION_CONSTANTS.SORT_KEYS.CREATED_DATE) {
      isCreatedDescSortedRef.current = undefined;
    }
    if (refNameToKeep !== SUBSCRIPTION_CONSTANTS.SORT_KEYS.PLAN) {
      isPlanAscSortedRef.current = undefined;
    }
  };

  const isMoveSubscriptionsDisabled = areAllRowsSelected ? false : selectedSubscriptionIds?.length === 0;

  const getNewFilterOptions = useCallback(
    async (
      searchInput?: string,
      selectedTab?: string,
      selectedStatusFilter?: { key: string; value: string }[] | Record<string, string>[],
      selectedPlanFilters?: { key: string; value: string }[] | Record<string, string>[],
      selectedRenewalFilters?: { key: string; value: string }[],
      selectedCurrency?: string
      // eslint-disable-next-line max-params
    ) => {
      const filterOptions: FilterOption[] = [];
      const tabToFilterKeyMap: any = {
        [SUBSCRIPTION_CONSTANTS.TAB_ENUM.ACTIVE]: 'status',
        [SUBSCRIPTION_CONSTANTS.TAB_ENUM.INACTIVE]: 'status',
        [SUBSCRIPTION_CONSTANTS.TAB_ENUM.FREE]: 'edition',
      };
      if (searchInput) {
        filterOptions.push({
          key: 'or',
          value: JSON.stringify(buildSearchFilters(searchInput)),
        });
      }
      if (selectedTab && selectedTab !== SUBSCRIPTION_CONSTANTS.TAB_ENUM.UPCOMING) {
        filterOptions.push({
          key: tabToFilterKeyMap[selectedTab],
          value: selectedTab,
        });
      }

      if (selectedTab && selectedTab === SUBSCRIPTION_CONSTANTS.TAB_ENUM.UPCOMING) {
        filterOptions.push({
          key: 'or',
          value: JSON.stringify([
            {
              fetchUpcomingSubscriptions: true,
            },
          ]),
        });
      }

      if (isDesignIteration1Enabled) {
        if (selectedStatusFilter && selectedStatusFilter?.length > 0) {
          const statusFilter: any = [];
          selectedStatusFilter.forEach((selectedFilter) => {
            // @ts-ignore
            const value = statusFilterMap[selectedFilter?.status];
            if (value) {
              statusFilter.push({ status: value });
            }
          });
          if (statusFilter.length > 0) {
            filterOptions.push({
              key: 'and',
              value: JSON.stringify([{ or: statusFilter }]),
            });
          }
          setStatusFilterOptions(selectedStatusFilter);
        } else if (isDefaultFiltersEnabled && !selectedStatusFilter?.length) {
          // user must have cleared all filters
          setStatusFilterOptions([]);
        }

        if (selectedPlanFilters && selectedPlanFilters?.length > 0) {
          const planFilter: any = [];
          selectedPlanFilters.forEach((selectedFilter) => {
            // @ts-ignore
            const value = planFilterMap[selectedFilter?.edition];
            if (value) {
              planFilter.push({ edition: value });
            }
          });
          if (planFilter.length > 0) {
            filterOptions.push({
              key: 'and',
              value: JSON.stringify([{ or: planFilter }]),
            });
          }
          setPlanFilterOptions(selectedPlanFilters);
        }
      }

      if (isSubscriptionRenewalDateFilterEnabled) {
        if (selectedRenewalFilters && selectedRenewalFilters?.length > 0) {
          selectedRenewalFilters.forEach((selectedFilter) => {
            filterOptions.push(selectedFilter);
          });
          setRenewalDateFilterOptions(selectedRenewalFilters);
        }
      }

      if (isMultiCurrencyChangesEnabled && selectedCurrency) {
        filterOptions.push({
          key: 'currency',
          value: selectedCurrency,
        });
      }
      return filterOptions;
    },
    [
      isDesignIteration1Enabled,
      isMultiCurrencyChangesEnabled,
      isSubscriptionRenewalDateFilterEnabled,
      isDefaultFiltersEnabled,
    ]
  );

  const SiteHead = ({ rowsInPage, isAppsInSearch }: SorterColumnHeadProps) => {
    const handleSorting = () => {
      makeAllSortedRefUndefinedButOne(SUBSCRIPTION_CONSTANTS.SORT_KEYS.CLOUD_SITE);
      isSiteAscSortedRef.current = !isSiteAscSortedRef.current;

      const newSortOptions = getSiteSortType(isSiteAscSortedRef.current);
      setCurrentPage(DEFAULT_PAGE);
      setSortOptions(newSortOptions);

      refetch(
        {
          filterOptions: filterOptions.current,
          searchOptions: {
            first: selectedTab === SUBSCRIPTION_CONSTANTS.TAB_ENUM.UPCOMING ? maxThresholdForResults : rowsInPage,
            offset: (DEFAULT_PAGE - 1) * rowsInPage,
            sort: newSortOptions,
          },
        },
        isAppsInSearch
      );
    };
    return (
      <SortableTableHead
        isAscSorted={isSiteAscSortedRef.current}
        handleSorting={handleSorting}
        columnName={SUBSCRIPTION_CONSTANTS.COLUMNS.SITE}
        className="col-site"
      />
    );
  };

  const RenewOnHead = ({ rowsInPage, isAppsInSearch }: SorterColumnHeadProps) => {
    const handleSorting = () => {
      setCurrentPage(DEFAULT_PAGE);
      makeAllSortedRefUndefinedButOne(SUBSCRIPTION_CONSTANTS.SORT_KEYS.RENEW_DATE);
      isRenewOnAscSortedRef.current = !isRenewOnAscSortedRef.current;

      const newSortOptions: SortType = getRenewOnSortType(isRenewOnAscSortedRef.current);
      setSortOptions(newSortOptions);

      refetch(
        {
          filterOptions: filterOptions.current,
          searchOptions: {
            first: rowsInPage,
            offset: (DEFAULT_PAGE - 1) * rowsInPage,
            sort: newSortOptions,
          },
        },
        isAppsInSearch
      );
    };

    return (
      <SortableTableHead
        isAscSorted={isRenewOnAscSortedRef.current}
        handleSorting={handleSorting}
        columnName={SUBSCRIPTION_CONSTANTS.COLUMNS.RENEW_ON}
        className="col-renew-in"
      />
    );
  };

  const CreatedHead = ({ rowsInPage, isAppsInSearch }: SorterColumnHeadProps) => {
    const handleSorting = () => {
      makeAllSortedRefUndefinedButOne(SUBSCRIPTION_CONSTANTS.SORT_KEYS.CREATED_DATE);
      isCreatedDescSortedRef.current = !isCreatedDescSortedRef.current;

      const newSortOptions: SortType = getCreatedSortType(isCreatedDescSortedRef.current);
      setSortOptions(newSortOptions);
      setCurrentPage(DEFAULT_PAGE);
      refetch(
        {
          filterOptions: filterOptions.current,
          searchOptions: {
            first: selectedTab === SUBSCRIPTION_CONSTANTS.TAB_ENUM.UPCOMING ? maxThresholdForResults : rowsInPage,
            offset: (DEFAULT_PAGE - 1) * rowsInPage,
            sort: newSortOptions,
          },
        },
        isAppsInSearch
      );
    };

    // handling the sortable field which can only take Ascending value related data
    // adding handing for method to render the column sort order properly
    const sortableFieldAscValue =
      isCreatedDescSortedRef.current !== undefined ? !isCreatedDescSortedRef.current : undefined;

    return (
      <SortableTableHead
        isAscSorted={sortableFieldAscValue}
        handleSorting={handleSorting}
        columnName={SUBSCRIPTION_CONSTANTS.COLUMNS.CREATED}
        className="col-renew-in"
      />
    );
  };

  const PlanHead = ({ rowsInPage, isAppsInSearch }: SorterColumnHeadProps) => {
    const handleSorting = () => {
      makeAllSortedRefUndefinedButOne(SUBSCRIPTION_CONSTANTS.SORT_KEYS.PLAN);
      isPlanAscSortedRef.current = !isPlanAscSortedRef.current;

      const newSortOptions = getPlanSortType(isPlanAscSortedRef.current);
      setCurrentPage(DEFAULT_PAGE);
      setSortOptions(newSortOptions);

      refetch(
        {
          filterOptions: filterOptions.current,
          searchOptions: {
            first: rowsInPage,
            offset: (DEFAULT_PAGE - 1) * rowsInPage,
            sort: newSortOptions,
          },
        },
        isAppsInSearch
      );
    };
    return (
      <SortableTableHead
        isAscSorted={isPlanAscSortedRef.current}
        handleSorting={handleSorting}
        columnName={SUBSCRIPTION_CONSTANTS.COLUMNS.OFFERING}
        className="col-plan"
      />
    );
  };

  React.useEffect(() => {
    if (selectedTab) {
      setColumnList(
        getTabSpecificHeaders(selectedTab as SUBSCRIPTION_CONSTANTS.TAB_ENUM, rowsPerPage, showAppsInSearch)
      );
    } else {
      setColumnList(getHeaders());
    }
  }, [areAllRowsSelected, selectedTab]);

  React.useEffect(() => {
    if (isDesignIteration1Enabled && !isRenewDateFilterVisible) {
      setRenewalDateFilterOptions([]);
      setSelectedRenewalDateRangeValue('');
    }
  }, [isDesignIteration1Enabled, isRenewDateFilterVisible]);

  React.useEffect(() => {
    // status filter will be applied by default which will show filter container
    // we should hide filter container when there's an error
    if (loading || !isDesignIteration1Enabled || !isDefaultFiltersEnabled) return;

    if (isStatusFilterAppliedByDefault) {
      if (error) {
        setIsStatusFilterAppliedByDefault(false);
      }
    }
  }, [isDesignIteration1Enabled, isStatusFilterAppliedByDefault, error, loading, isDefaultFiltersEnabled]);

  const headersMap = useCallback(
    (rowsInPage: SorterColumnHeadProps['rowsInPage'], isAppsInSearch: SorterColumnHeadProps['isAppsInSearch']) => {
      return {
        [SUBSCRIPTION_CONSTANTS.COLUMNS.SUBSCRIPTION]: {
          name: SUBSCRIPTION_CONSTANTS.COLUMNS.SUBSCRIPTION,
          ...(isRowSelectionRequired &&
          (isDesignIteration1Enabled || selectedTab === SUBSCRIPTION_CONSTANTS.TAB_ENUM.ACTIVE)
            ? {
                renderComponent: (
                  <th className="row-selection-container">
                    <Checkbox
                      className="selection-checkbox"
                      isChecked={areAllRowsSelected}
                      testId={`select-subscription-checkbox-all`}
                      onChange={onAllRowSelected}
                      label=""
                    />
                    <div className="product-name-header">{SUBSCRIPTION_CONSTANTS.COLUMNS.SUBSCRIPTION}</div>
                  </th>
                ),
              }
            : { content: SUBSCRIPTION_CONSTANTS.COLUMNS.SUBSCRIPTION }),
          customClassNames:
            isRowSelectionRequired &&
            (isDesignIteration1Enabled || selectedTab === SUBSCRIPTION_CONSTANTS.TAB_ENUM.ACTIVE)
              ? 'col-subscription with-row-selection'
              : 'col-subscription',
        },
        [SUBSCRIPTION_CONSTANTS.COLUMNS.STATUS]: {
          name: SUBSCRIPTION_CONSTANTS.COLUMNS.STATUS,
          content: SUBSCRIPTION_CONSTANTS.COLUMNS.STATUS,
          customClassNames: 'col-status',
        },
        [SUBSCRIPTION_CONSTANTS.COLUMNS.OFFERING]: {
          name: SUBSCRIPTION_CONSTANTS.COLUMNS.OFFERING,
          content: SUBSCRIPTION_CONSTANTS.COLUMNS.OFFERING,
          customClassNames: 'col-plan',
          isMandatory: false,
          isChecked: true,
          ...(isDesignIteration1Enabled && {
            renderComponent: <PlanHead rowsInPage={rowsInPage} isAppsInSearch={isAppsInSearch} />,
          }),
        },
        [SUBSCRIPTION_CONSTANTS.COLUMNS.APPS]: {
          name: SUBSCRIPTION_CONSTANTS.COLUMNS.APPS,
          content: SUBSCRIPTION_CONSTANTS.COLUMNS.APPS,
          customClassNames: 'col-apps',
          ...(isDesignIteration1Enabled && {
            renderComponent: <th className="text-align-right">{SUBSCRIPTION_CONSTANTS.COLUMNS.APPS}</th>,
          }),
        },
        [SUBSCRIPTION_CONSTANTS.COLUMNS.ORGANIZATION_NAME]: {
          name: SUBSCRIPTION_CONSTANTS.COLUMNS.ORGANIZATION_NAME,
          content: SUBSCRIPTION_CONSTANTS.COLUMNS.ORGANIZATION_NAME,
          customClassNames: isDesignIteration1Enabled ? 'col-org' : 'col-site',
        },
        [SUBSCRIPTION_CONSTANTS.COLUMNS.SITE]: {
          name: SUBSCRIPTION_CONSTANTS.COLUMNS.SITE,
          content: SUBSCRIPTION_CONSTANTS.COLUMNS.SITE,
          customClassNames: 'col-site',
          renderComponent: <SiteHead rowsInPage={rowsInPage} isAppsInSearch={isAppsInSearch} />,
        },
        [SUBSCRIPTION_CONSTANTS.COLUMNS.COMPANY]: {
          name: SUBSCRIPTION_CONSTANTS.COLUMNS.COMPANY,
          content: SUBSCRIPTION_CONSTANTS.COLUMNS.COMPANY,
          customClassNames: 'col-common col-company',
        },
        [SUBSCRIPTION_CONSTANTS.COLUMNS.SOLD_TO_ADDRESS]: {
          name: SUBSCRIPTION_CONSTANTS.COLUMNS.SOLD_TO_ADDRESS,
          content: SUBSCRIPTION_CONSTANTS.COLUMNS.SOLD_TO_ADDRESS,
          customClassNames: 'col-sold-to-address',
        },
        [SUBSCRIPTION_CONSTANTS.COLUMNS.CUSTOMER_CONTACTS]: {
          name: SUBSCRIPTION_CONSTANTS.COLUMNS.CUSTOMER_CONTACTS,
          content: SUBSCRIPTION_CONSTANTS.COLUMNS.CUSTOMER_CONTACTS,
          isMandatory: false,
          isChecked: true,
          customClassNames: 'col-common',
        },
        [SUBSCRIPTION_CONSTANTS.COLUMNS.BILLED_USERS]: {
          name: SUBSCRIPTION_CONSTANTS.COLUMNS.BILLED_USERS,
          content: SUBSCRIPTION_CONSTANTS.COLUMNS.BILLED_USERS,
          customClassNames: 'col-common',
        },
        [SUBSCRIPTION_CONSTANTS.COLUMNS.RENEW_ON]: {
          name: SUBSCRIPTION_CONSTANTS.COLUMNS.RENEW_ON,
          content: SUBSCRIPTION_CONSTANTS.COLUMNS.RENEW_ON,
          customClassNames: 'col-common col-renew-date',
          renderComponent: <RenewOnHead rowsInPage={rowsInPage} isAppsInSearch={isAppsInSearch} />,
        },
        [SUBSCRIPTION_CONSTANTS.COLUMNS.BILLING_CYCLE]: {
          name: SUBSCRIPTION_CONSTANTS.COLUMNS.BILLING_CYCLE,
          content: SUBSCRIPTION_CONSTANTS.COLUMNS.BILLING_CYCLE,
          customClassNames: 'col-common col-billing-cycle',
          isMandatory: false,
          isChecked: true,
        },
        [SUBSCRIPTION_CONSTANTS.COLUMNS.ENTITLEMENT_NUMBER]: {
          name: SUBSCRIPTION_CONSTANTS.COLUMNS.ENTITLEMENT_NUMBER,
          content: SUBSCRIPTION_CONSTANTS.COLUMNS.ENTITLEMENT_NUMBER,
          customClassNames: 'col-common',
        },
        [SUBSCRIPTION_CONSTANTS.COLUMNS.CREATED]: {
          name: SUBSCRIPTION_CONSTANTS.COLUMNS.CREATED,
          content: SUBSCRIPTION_CONSTANTS.COLUMNS.CREATED,
          customClassNames: 'col-common',
          renderComponent: <CreatedHead rowsInPage={rowsInPage} isAppsInSearch={isAppsInSearch} />,
        },
        [SUBSCRIPTION_CONSTANTS.COLUMNS.LAST_INVOICE_AMOUNT]: {
          name: SUBSCRIPTION_CONSTANTS.COLUMNS.LAST_INVOICE_AMOUNT,
          content: SUBSCRIPTION_CONSTANTS.COLUMNS.LAST_INVOICE_AMOUNT,
          customClassNames: 'col-common col-last-invoice-amount',
          isMandatory: false,
          isChecked: true,
          renderComponent: isDesignIteration1Enabled ? (
            <th className="text-align-right">{SUBSCRIPTION_CONSTANTS.COLUMNS.LAST_INVOICE_AMOUNT}</th>
          ) : null,
        },
        [SUBSCRIPTION_CONSTANTS.COLUMNS.CUSTOMER]: {
          name: SUBSCRIPTION_CONSTANTS.COLUMNS.CUSTOMER,
          content: SUBSCRIPTION_CONSTANTS.COLUMNS.CUSTOMER,
          customClassNames: 'col-customer',
        },
        [SUBSCRIPTION_CONSTANTS.COLUMNS.BILLING_PROFILE]: {
          name: SUBSCRIPTION_CONSTANTS.COLUMNS.BILLING_PROFILE,
          content: SUBSCRIPTION_CONSTANTS.COLUMNS.BILLING_PROFILE,
          customClassNames: 'col-billing-profile',
        },
        [SUBSCRIPTION_CONSTANTS.COLUMNS.USAGE]: {
          name: SUBSCRIPTION_CONSTANTS.COLUMNS.USAGE,
          content: SUBSCRIPTION_CONSTANTS.COLUMNS.USAGE,
          customClassNames: 'col-usage',
        },
        [SUBSCRIPTION_CONSTANTS.COLUMNS.ADD_ONS]: {
          name: SUBSCRIPTION_CONSTANTS.COLUMNS.ADD_ONS,
          content: SUBSCRIPTION_CONSTANTS.COLUMNS.ADD_ONS,
          customClassNames: isDesignIteration1Enabled ? 'col-add-ons' : 'col-apps',
          ...(isDesignIteration1Enabled && {
            renderComponent: <th className="text-align-right">{SUBSCRIPTION_CONSTANTS.COLUMNS.ADD_ONS}</th>,
          }),
        },
        [SUBSCRIPTION_CONSTANTS.COLUMNS.INCLUDED_PRODUCTS]: {
          name: SUBSCRIPTION_CONSTANTS.COLUMNS.INCLUDED_PRODUCTS,
          content: SUBSCRIPTION_CONSTANTS.COLUMNS.INCLUDED_PRODUCTS,
          customClassNames: 'col-common col-included-products',
        },
      };
    },
    [onAllRowSelected, areAllRowsSelected, isRowSelectionRequired, selectedTab]
  );

  const getHeaders = (
    rowsInPage: SorterColumnHeadProps['rowsInPage'] = rowsPerPage,
    isAppsInSearch: SorterColumnHeadProps['isAppsInSearch'] = showAppsInSearch
  ) => {
    const updateColumnsToDisplay = (columns: any[]) => {
      let updatedColumns = columns;
      if (isTWCPhase1Enabled) {
        updatedColumns = updatedColumns.filter(
          (column) =>
            ![SUBSCRIPTION_CONSTANTS.COLUMNS.APPS, SUBSCRIPTION_CONSTANTS.COLUMNS.ADD_ONS].includes(column.name)
        );
        const offeringIndex = updatedColumns.findIndex(
          (column) => column.name === SUBSCRIPTION_CONSTANTS.COLUMNS.OFFERING
        );
        if (offeringIndex >= 0) {
          updatedColumns.splice(offeringIndex + 1, 0, columnHeaders[SUBSCRIPTION_CONSTANTS.COLUMNS.INCLUDED_PRODUCTS]);
        }
      }
      return updatedColumns;
    };

    const columnHeaders = headersMap(rowsInPage, isAppsInSearch);

    if (isDesignIteration1Enabled) {
      const updatedColumns = columnsToShow.map((column) => columnHeaders[column]);
      return updateColumnsToDisplay(updatedColumns);
    }

    const allColumns = [
      columnHeaders[SUBSCRIPTION_CONSTANTS.COLUMNS.SUBSCRIPTION],
      columnHeaders[SUBSCRIPTION_CONSTANTS.COLUMNS.STATUS],
      columnHeaders[SUBSCRIPTION_CONSTANTS.COLUMNS.OFFERING],
      columnHeaders[SUBSCRIPTION_CONSTANTS.COLUMNS.ADD_ONS],
      columnHeaders[SUBSCRIPTION_CONSTANTS.COLUMNS.APPS],
      columnHeaders[SUBSCRIPTION_CONSTANTS.COLUMNS.ORGANIZATION_NAME],
      columnHeaders[SUBSCRIPTION_CONSTANTS.COLUMNS.SITE],
      columnHeaders[SUBSCRIPTION_CONSTANTS.COLUMNS.COMPANY],
      columnHeaders[SUBSCRIPTION_CONSTANTS.COLUMNS.SOLD_TO_ADDRESS],
      columnHeaders[SUBSCRIPTION_CONSTANTS.COLUMNS.BILLED_USERS],
      columnHeaders[SUBSCRIPTION_CONSTANTS.COLUMNS.RENEW_ON],
      columnHeaders[SUBSCRIPTION_CONSTANTS.COLUMNS.BILLING_CYCLE],
      columnHeaders[SUBSCRIPTION_CONSTANTS.COLUMNS.ENTITLEMENT_NUMBER],
      columnHeaders[SUBSCRIPTION_CONSTANTS.COLUMNS.CREATED],
      columnHeaders[SUBSCRIPTION_CONSTANTS.COLUMNS.LAST_INVOICE_AMOUNT],
    ];

    return updateColumnsToDisplay(allColumns);
  };

  const [columnsList, setColumnList] = useState<Array<ColumnList>>(getHeaders());

  const getTabSpecificHeaders = (
    tabLabel: SUBSCRIPTION_CONSTANTS.TAB_ENUM,
    rowsInPage?: SorterColumnHeadProps['rowsInPage'],
    isAppsInSearch?: SorterColumnHeadProps['isAppsInSearch']
  ) => {
    const tabSpecificColumnsMap = SUBSCRIPTION_CONSTANTS.TAB_SPECIFIC_COLUMNS[tabLabel];
    const columns: (HeadCell & SearchResultColumn)[] = [];
    getHeaders(rowsInPage, isAppsInSearch).forEach((columnData) => {
      if (tabSpecificColumnsMap[columnData.name]) {
        columns.push(columnData);
      }
    });
    return columns;
  };

  const onPageChange = useCallback(
    (pageNumber: number) => {
      setCurrentPage(pageNumber);
      refetch(
        {
          searchOptions: {
            first: rowsPerPage,
            offset: (pageNumber - 1) * rowsPerPage,
            sort: sortOptions,
          },
          filterOptions: filterOptions.current,
        },
        showAppsInSearch
      );
    },
    [refetch, rowsPerPage, sortOptions, showAppsInSearch]
  );

  const renderBulkSubscriptionTransfer = useCallback(() => {
    if (isBulkTransferButtonRequired) {
      return (
        <TransferSubscriptionsInBulk
          isMoveSubscriptionsDisabled={isMoveSubscriptionsDisabled}
          onMoveSubscriptionsCompleted={() => {
            refetch({
              filterOptions: filterOptions.current,
              searchOptions: {
                first: rowsPerPage,
                offset: (currentPage - 1) * rowsPerPage,
              },
            });
            resetRowSelections?.();
          }}
          dataState={dataState}
          areAllRowsSelected={areAllRowsSelected}
          selectedSubscriptionIds={selectedSubscriptionIds ?? []}
          iconBefore={
            //@ts-ignore
            isDesignIteration1Enabled && <img src={MoveBulkIcon} alt="support issue icon" className="support-icon" />
          }
          shouldAllowBillingProfileTransferBasedOnPricingEligibility={
            shouldAllowBillingProfileTransferBasedOnPricingEligibility
          }
          allowedPricingEligibilityForBillingProfileTransfer={allowedPricingEligibilityForBillingProfileTransfer}
        />
      );
    }
    return <></>;
  }, [
    selectedTab,
    isMoveSubscriptionsDisabled,
    filterOptions,
    rowsPerPage,
    currentPage,
    dataState,
    areAllRowsSelected,
    selectedSubscriptionIds,
    isDesignIteration1Enabled,
  ]);

  const handleRowsChange = useCallback(
    (rowsCount: number) => {
      setRowsPerPage(rowsCount);
      setCurrentPage(DEFAULT_PAGE);
      if (selectedTab) {
        setColumnList(
          getTabSpecificHeaders(selectedTab as SUBSCRIPTION_CONSTANTS.TAB_ENUM, rowsCount, showAppsInSearch)
        );
      }
      refetch(
        {
          searchOptions: {
            first: rowsCount,
            offset: 0,
            sort: sortOptions,
          },
          filterOptions: filterOptions.current,
        },
        showAppsInSearch
      );
    },
    [getTabSpecificHeaders, selectedTab, showAppsInSearch, refetch, sortOptions]
  );

  const onSearch = useCallback(
    async (formData: FormData) => {
      const searchInput = formData?.searchTerm;
      let newFilterOptions: FilterOption[] = [];
      // in case there is no search input, don't show apps again
      const showAppsInSearch = !!searchInput;
      const selectedStatusFilters = statusFilterOptions.length > 0 ? [...statusFilterOptions] : [];
      const selectedPlanFilters = planFilterOptions.length > 0 ? [...planFilterOptions] : [];
      const selectedRenewalFilters = renewalDateFilterOptions.length > 0 ? [...renewalDateFilterOptions] : [];
      setShowAppsInSearch(showAppsInSearch);
      if (selectedTab) {
        setColumnList(
          getTabSpecificHeaders(selectedTab as SUBSCRIPTION_CONSTANTS.TAB_ENUM, rowsPerPage, showAppsInSearch)
        );
      }

      if (searchInput) {
        trackSearchEvent();
      }

      setSearchInputText(searchInput);
      setIsSearched(!!searchInput);
      newFilterOptions = await getNewFilterOptions(
        searchInput,
        selectedTab,
        selectedStatusFilters,
        selectedPlanFilters,
        selectedRenewalFilters,
        currency
      );
      filterOptions.current = newFilterOptions;
      setCurrentPage(DEFAULT_PAGE);
      refetch(
        {
          filterOptions: newFilterOptions,
          searchOptions: {
            first: selectedTab === SUBSCRIPTION_CONSTANTS.TAB_ENUM.UPCOMING ? maxThresholdForResults : rowsPerPage,
            offset: 0,
            sort: sortOptions,
          },
        },
        showAppsInSearch
      );
    },
    [
      statusFilterOptions,
      planFilterOptions,
      selectedTab,
      getNewFilterOptions,
      refetch,
      rowsPerPage,
      sortOptions,
      getTabSpecificHeaders,
      renewalDateFilterOptions,
    ]
  );

  const renderActions = useCallback(() => {
    const csvFilterOptions = [...(downloadCSVFilter || []), ...filterOptions.current];
    return [
      () =>
        // actions on tables expecting jsx element to be passed or null
        shouldHideDownloadCsv || !transactionAccountId ? (
          <></>
        ) : (
          <div className="action-buttons" data-testId="action-download-csv">
            <DownloadCSVPerfV2
              transactionAccountId={transactionAccountId}
              filterOptions={csvFilterOptions}
              buttonText={'Export as CSV'}
              isDarkTheme={false}
              sortOptions={sortOptions}
              relatedDomains={relatedDomains}
              shouldFetchInternalLicenses={shouldFetchInternalLicenses}
            />
          </div>
        ),
      ...(!isDesignIteration1Enabled ? [() => renderBulkSubscriptionTransfer()] : []),
    ];
  }, [
    downloadCSVFilter,
    shouldHideDownloadCsv,
    transactionAccountId,
    sortOptions,
    isMoveSubscriptionsDisabled,
    selectedSubscriptionIds,
    areAllRowsSelected,
  ]);

  const helperMessage = (() => {
    return `Search by E-#, company, address, plan, org, URL, or ${isAppificationEnabled ? 'app name' : 'product'}`;
  })();

  const handleStatusFilter = async (
    selectedStatusFilters: { key: string; value: string }[] | Record<string, string>[]
  ) => {
    if (isDesignIteration1Enabled && isDefaultFiltersEnabled) {
      if (!selectedStatusFilters.length) {
        // this will not set default values in select field
        setStatusFilterResetCount((prev) => prev + 1);
      } else if (statusFilterResetCount) {
        // user must have clicked clear reset filters once, hence reseting the value for clear reset to work again
        setStatusFilterResetCount(0);
      }

      setIsStatusFilterAppliedByDefault(false);
    }

    let newFilterOptions: FilterOption[] = [];
    // in case there is no search input, don't show apps again
    const showAppsInSearch = !!searchInputText;

    const selectedPlanFilters = planFilterOptions.length > 0 ? [...planFilterOptions] : [];
    let selectedRenewalFilters = renewalDateFilterOptions.length > 0 ? [...renewalDateFilterOptions] : [];

    if (isDesignIteration1Enabled) {
      const isActiveStatusSelected = selectedStatusFilters?.some(
        (filter) => filter?.status === SubscriptionStatusType.ACTIVE
      );
      setIsRenewDateFilterVisible(isActiveStatusSelected);
      if (!isActiveStatusSelected) {
        selectedRenewalFilters = [];
      }
    }

    setShowAppsInSearch(showAppsInSearch);
    if (selectedTab) {
      setColumnList(
        getTabSpecificHeaders(selectedTab as SUBSCRIPTION_CONSTANTS.TAB_ENUM, rowsPerPage, showAppsInSearch)
      );
    }

    setIsStatusFilterApplied(selectedStatusFilters.length > 0);

    newFilterOptions = await getNewFilterOptions(
      searchInputText,
      selectedTab,
      [...selectedStatusFilters],
      selectedPlanFilters,
      selectedRenewalFilters,
      currency
    );
    filterOptions.current = newFilterOptions;
    setCurrentPage(DEFAULT_PAGE);
    refetch(
      {
        filterOptions: newFilterOptions,
        searchOptions: {
          first: rowsPerPage,
          offset: 0,
          sort: sortOptions,
        },
      },
      showAppsInSearch
    );
  };

  const handlePlanFilter = async (selectedPlanFilters: { key: string; value: string }[] | Record<string, string>[]) => {
    let newFilterOptions: FilterOption[] = [];
    // in case there is no search input, don't show apps again
    const showAppsInSearch = !!searchInputText;

    setShowAppsInSearch(showAppsInSearch);
    if (selectedTab) {
      setColumnList(
        getTabSpecificHeaders(selectedTab as SUBSCRIPTION_CONSTANTS.TAB_ENUM, rowsPerPage, showAppsInSearch)
      );
    }

    const selectedStatusFilters = statusFilterOptions.length > 0 ? [...statusFilterOptions] : [];
    const selectedRenewalFilters = renewalDateFilterOptions.length > 0 ? [...renewalDateFilterOptions] : [];

    setIsPlanFilterApplied(selectedPlanFilters.length > 0);

    newFilterOptions = await getNewFilterOptions(
      searchInputText,
      selectedTab,
      selectedStatusFilters,
      [...selectedPlanFilters],
      selectedRenewalFilters,
      currency
    );
    filterOptions.current = newFilterOptions;
    setCurrentPage(DEFAULT_PAGE);
    refetch(
      {
        filterOptions: newFilterOptions,
        searchOptions: {
          first: rowsPerPage,
          offset: 0,
          sort: sortOptions,
        },
      },
      showAppsInSearch
    );
  };

  const renderStatusFilter = () => {
    return (
      <CustomFilterCheckboxWrapper
        filterFields={statusFilterFields}
        handleFilterUpdate={handleStatusFilter}
        shouldHideClearFilterButton={true}
        resetFromParent={statusFilterResetCount}
        version={isDesignIteration1Enabled && isDefaultFiltersEnabled ? 2 : undefined}
      />
    );
  };

  const handleCurrencyFilter = async (selectedCurrency: string) => {
    let newFilterOptions: FilterOption[] = [];
    const showAppsInSearch = !!searchInputText;
    const selectedPlanFilters = planFilterOptions.length > 0 ? [...planFilterOptions] : [];
    const selectedRenewalFilters = renewalDateFilterOptions.length > 0 ? [...renewalDateFilterOptions] : [];
    const selectedStatusFilters = statusFilterOptions.length > 0 ? [...statusFilterOptions] : [];

    setShowAppsInSearch(showAppsInSearch);

    if (selectedTab) {
      setColumnList(
        getTabSpecificHeaders(selectedTab as SUBSCRIPTION_CONSTANTS.TAB_ENUM, rowsPerPage, showAppsInSearch)
      );
    }

    newFilterOptions = await getNewFilterOptions(
      searchInputText,
      selectedTab,
      selectedStatusFilters,
      selectedPlanFilters,
      selectedRenewalFilters,
      selectedCurrency
    );
    filterOptions.current = newFilterOptions;
    setCurrentPage(DEFAULT_PAGE);
    refetch(
      {
        filterOptions: newFilterOptions,
        searchOptions: {
          first: rowsPerPage,
          offset: 0,
          sort: sortOptions,
        },
      },
      showAppsInSearch
    );
  };

  const renderPlanFilter = () => {
    return (
      <CustomFilterCheckboxWrapper
        filterFields={planFilterFields}
        handleFilterUpdate={handlePlanFilter}
        shouldHideClearFilterButton={true}
        resetFromParent={planFilterResetCount}
      />
    );
  };

  const handleDateRangeFilter = async (key: string, value: DateRangeType, isCustomFilter?: boolean) => {
    let newFilterOptions: FilterOption[] = [];
    // in case there is no search input, don't show apps again
    const showAppsInSearch = !!searchInputText;

    setShowAppsInSearch(showAppsInSearch);

    if (selectedTab) {
      setColumnList(
        getTabSpecificHeaders(selectedTab as SUBSCRIPTION_CONSTANTS.TAB_ENUM, rowsPerPage, showAppsInSearch)
      );
    }

    const fromDate = {
      key: key + 'IsGte',
      value: isCustomFilter ? value.from : value.to,
    };
    const toDate = {
      key: key + 'IsLte',
      value: isCustomFilter ? value.to : value.from,
    };

    const newRenewalDateFilterOptions = [fromDate, toDate];

    if (key === dateRangeKeys.renewalDate) {
      setRenewalDateFilterOptions(newRenewalDateFilterOptions);
      setIsRenewalFilterApplied(true);
    }

    const selectedStatusFilters = statusFilterOptions.length > 0 ? [...statusFilterOptions] : [];
    const selectedPlanFilters = planFilterOptions.length > 0 ? [...planFilterOptions] : [];

    newFilterOptions = await getNewFilterOptions(
      searchInputText,
      selectedTab,
      selectedStatusFilters,
      selectedPlanFilters,
      [...newRenewalDateFilterOptions],
      currency
    );

    filterOptions.current = newFilterOptions;
    setCurrentPage(DEFAULT_PAGE);
    refetch(
      {
        filterOptions: newFilterOptions,
        searchOptions: {
          first: rowsPerPage,
          offset: 0,
          sort: sortOptions,
        },
      },
      showAppsInSearch
    );
    setCurrentPage(DEFAULT_PAGE);
  };

  const getAppropriateDateRangeValue = (value: any) => {
    value.to = getFormattedDateFromTimeStamp(value.to, false);
    value.from = getFormattedDateFromTimeStamp(value.from, false);
    return value;
  };

  const getRenewalDateRange = (value: any, isCustomFilter?: boolean) => {
    const rangeValue = getAppropriateDateRangeValue(value);
    handleDateRangeFilter(dateRangeKeys.renewalDate, rangeValue, isCustomFilter);
  };

  const renderRenewalDateFilter = () => {
    return (
      <DateFilterWithBadge
        dateValue={getRenewalDateRange}
        selected={selectedRenewalDateRangeValue}
        onSelected={(selected: string) => {
          setSelectedRenewalDateRangeValue(selected);
        }}
        placeholder="Renew in"
        dateRangeOptions={renewalDateRangeOption}
      />
    );
  };

  const renderCurrencyFilter = () => {
    return (
      <div>
        <LimitCurrencyDropdown
          currency={currency}
          setCurrency={(value) => {
            const selectedCurrency = value ? (value as string) : '';
            setCurrency(selectedCurrency);
            handleCurrencyFilter(selectedCurrency);
          }}
          showLabel={false}
          isLightTheme
          isFilter
          showAllCurrency
          shouldPrependCurrencyText={isDesignIteration1Enabled}
          isDesignIteration1Enabled={isDesignIteration1Enabled}
        />
      </div>
    );
  };

  const isAnyFilterApplied =
    (isDesignIteration1Enabled && isDefaultFiltersEnabled && isStatusFilterAppliedByDefault) ||
    isStatusFilterApplied ||
    isPlanFilterApplied ||
    isRenewalFilterApplied ||
    isCurrencyFilterApplied;
  const clearAllFilters = async () => {
    setCurrentPage(DEFAULT_PAGE);
    setStatusFilterResetCount(statusFilterResetCount + 1);
    setPlanFilterResetCount(planFilterResetCount + 1);
    setStatusFilterFields(statusFilterCommonFields);
    setPlanFilterFields(initialPlanFilterFields);
    setPlanFilterOptions([]);
    setStatusFilterOptions([
      { status: SubscriptionStatusType.ACTIVE },
      ...(isFutureDatedSubscriptionsEnabled ? [{ status: SubscriptionStatusType.UPCOMING }] : []),
    ]);
    setIsStatusFilterApplied(false);
    setIsPlanFilterApplied(false);
    setIsRenewalFilterApplied(false);
    setSelectedRenewalDateRangeValue('');
    setRenewalDateFilterOptions([]);
    setCurrency('');
    setIsRenewDateFilterVisible(true);
    if (isDefaultFiltersEnabled) {
      setInitialSelectedFieldsForStatusFilter([]);
      setIsStatusFilterAppliedByDefault(false);
    }

    const showAppsInSearch = !!searchInputText;
    setShowAppsInSearch(showAppsInSearch);

    const newFilterOptions: FilterOption[] = await getNewFilterOptions(searchInputText, selectedTab, []);
    filterOptions.current = newFilterOptions;

    refetch(
      {
        filterOptions: newFilterOptions,
        searchOptions: {
          first: rowsPerPage,
          offset: 0,
          sort: sortOptions,
        },
      },
      showAppsInSearch
    );
  };

  const renderFilters = () => {
    return (
      <div
        className={classNames({
          'ppc-subscription-table-filter': !isDesignIteration1Enabled,
          'ppc-subscription-table-filter-v2': isDesignIteration1Enabled,
        })}
      >
        <CustomSearch
          placeholder="Search"
          onSearch={onSearch}
          isLoading={loading}
          isClearable={true}
          helperMessage={helperMessage}
          shouldTrimInput={isRedesignFastFollowEnabled}
        />
        {isDesignIteration1Enabled && renderStatusFilter()}
        {isDesignIteration1Enabled && renderPlanFilter()}
        {isMultiCurrencyChangesEnabled && isDesignIteration1Enabled && renderCurrencyFilter()}
        {isSubscriptionRenewalDateFilterEnabled &&
          isDesignIteration1Enabled &&
          isRenewDateFilterVisible &&
          renderRenewalDateFilter()}
        {isSubscriptionRenewalDateFilterEnabled &&
          selectedTab === SUBSCRIPTION_CONSTANTS.TAB_ENUM.ACTIVE &&
          renderRenewalDateFilter()}
        {isMultiCurrencyChangesEnabled &&
          selectedTab === SUBSCRIPTION_CONSTANTS.TAB_ENUM.ACTIVE &&
          renderCurrencyFilter()}
        {(isDesignIteration1Enabled || isSubscriptionRenewalDateFilterEnabled) && isAnyFilterApplied && (
          <Button appearance="link" testId="invoice-filter-tab-reset-link" onClick={clearAllFilters}>
            Clear all filters
          </Button>
        )}
      </div>
    );
  };

  const renderTabTable = (tabLabel: SUBSCRIPTION_CONSTANTS.TAB_ENUM) => {
    const children = (
      <>
        <ColGroup columns={getSelectedColumns(columnsList)} />
        <TableHead columns={getSelectedColumns(columnsList)} />
        <TableBody>
          {data?.map((item: generatedTypesNew.ProductAppSubscription) => {
            return item?.products?.map((product: any) => {
              const isGracePeriodEntitlement = isEntitlementOnGracePeriod(product);

              return (
                <SubscriptionTableRow
                  key={product.id + tabLabel}
                  product={product}
                  transactionAccountId={transactionAccountId}
                  columnsList={columnsList}
                  isSandboxSupportForSubscriptionsEnabled={isSandboxSupportForSubscriptionsEnabled}
                  isNonCommercialPricingEnabled={isNonCommercialPricingEnabled}
                  {...(isDesignIteration1Enabled && { subscriptionColumnVersion: 2 })}
                  {...(isRowSelectionRequired && {
                    isRowSelectionRequired:
                      isDesignIteration1Enabled || tabLabel === SUBSCRIPTION_CONSTANTS.TAB_ENUM.ACTIVE,
                  })}
                  {...(isRowSelectionRequired && { onRowSelected })}
                  {...(isRowSelectionRequired && {
                    isRowSelected: areAllRowsSelected
                      ? areAllRowsSelected
                      : selectedSubscriptionIds?.includes(product.id),
                  })}
                  isFutureDatedSubscriptionsEnabled={isFutureDatedSubscriptionsEnabled}
                  eccDetails={get(eccDetails, product.billingGroupId, [])}
                  relatedDomains={relatedDomains}
                  isInternalUsageLicensesEnabled={isInternalUsageLicensesEnabled}
                  isSupportForManuallyBilledSubsEnabled={true}
                  isDesignIteration1Enabled={isDesignIteration1Enabled}
                  isEntitlementOnGracePeriod={isGracePeriodVisibilityEnabled ? isGracePeriodEntitlement : false}
                  isAppificationEnabled={isAppificationEnabled}
                />
              );
            });
          })}
        </TableBody>
      </>
    );

    const emptyStatePropForSearch = isDesignIteration1Enabled ? emptyStatePropForWrongSearch : defaultEmptyStateProp;
    const emptyStateProp = isDesignIteration1Enabled ? emptyStatePropForSubscriptionTableFlat : defaultEmptyStateProp;

    const renderMoveSelectedSubscription = () => {
      if (!loading && isDesignIteration1Enabled && (selectedSubscriptionIds?.length || areAllRowsSelected)) {
        return (
          <div className="subscription-move-btn-container">
            <span>{areAllRowsSelected ? totalCount : selectedSubscriptionIds?.length} selected</span>
            {renderBulkSubscriptionTransfer()}
          </div>
        );
      }
      return <></>;
    };

    const shouldEnablePagination = () => {
      if (tabLabel === SUBSCRIPTION_CONSTANTS.TAB_ENUM.UPCOMING) {
        return false;
      }
      return typeof isPaginationEnabled !== 'undefined' ? isPaginationEnabled : totalCount > DEFAULT_PAGE_SIZE;
    };

    const isOfferingPresentOnColumnsList =
      getSelectedColumns(columnsList).findIndex(
        (column: { name: string }) => column.name === SUBSCRIPTION_CONSTANTS.COLUMNS.OFFERING
      ) >= 0;

    return (
      <CustomDynamicResultTable
        className={classNames('ppc-subscription-table', {
          // All sticky styles are to be handled in sticky-table-with-shadow class for old design
          'sticky-table-with-shadow': !isDesignIteration1Enabled,
          'ppc-subscription-table-v2': isDesignIteration1Enabled,
          // Cleanup ppc-subscription-table-with-plan-and-included-products after design iteration phase 1 is released
          'ppc-subscription-table-with-plan-and-included-products':
            !isDesignIteration1Enabled && isTWCPhase1Enabled && isOfferingPresentOnColumnsList,
          // Cleanup ppc-subscription-table-with-included-products after design iteration phase 1 is released
          'ppc-subscription-table-with-included-products':
            !isDesignIteration1Enabled && isTWCPhase1Enabled && !isOfferingPresentOnColumnsList,
          // Cleanup ppc-subscription-table-with-add-ons after TWC Phase 1 release
          'ppc-subscription-table-with-add-ons': isAddOnsEnabled && !isDesignIteration1Enabled && !isTWCPhase1Enabled,
          'ppc-subsciptions-table-for-future-dated-subscriptions':
            tabLabel === SUBSCRIPTION_CONSTANTS.TAB_ENUM.UPCOMING,
        })}
        emptyStateProps={isSearched ? emptyStatePropForSearch : emptyStateProp}
        customiseColumnView={
          shouldHideCustomiseView ? (
            <></>
          ) : (
            <CustomizeView
              columns={columnsList}
              columnCount={columnsList.length}
              updatedColumnView={(record: Array<HeadCell>) => setColumnList([...record] as Array<ColumnList>)}
            />
          )
        }
        isLoading={loading}
        noResults={hasNoResults}
        error={error}
        totalCount={totalCount}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        onPageChange={onPageChange}
        onRowChange={handleRowsChange}
        isPaginationEnabled={shouldEnablePagination()}
        actions={renderActions()}
        hasTableWrapper={true}
        shouldShowHorizontalScrollAnimation={true}
        testId={testId}
        shouldForceEnableFilters={true}
        filters={renderFilters()}
        errorStateProps={errorStateProp}
        isSearched={isSearched}
        actionsAfterFilter={renderMoveSelectedSubscription()}
        isAnyFilterApplied={isAnyFilterApplied}
      >
        {children}
      </CustomDynamicResultTable>
    );
  };

  const handleSortOnTabSelect = (tabLabel: string): SortType => {
    if (tabLabel !== SUBSCRIPTION_CONSTANTS.TAB_ENUM.ACTIVE) {
      isSiteAscSortedRef.current = DefaultIsSiteAscSorted;
      isRenewOnAscSortedRef.current = undefined;
      isCreatedDescSortedRef.current = undefined;
      return getSiteSortType(DefaultIsSiteAscSorted);
    }

    isRenewOnAscSortedRef.current = DefaultIsRenewOnAscSorted;
    isSiteAscSortedRef.current = undefined;
    isCreatedDescSortedRef.current = undefined;
    return getRenewOnSortType(DefaultIsRenewOnAscSorted);
  };

  const onTabSelect = async (tabLabel: string) => {
    setSelectedTab(tabLabel);
    const newSortType = handleSortOnTabSelect(tabLabel);
    const newFilterOptions: FilterOption[] = getFilterOptions('', tabLabel);
    filterOptions.current = newFilterOptions;
    setColumnList(getTabSpecificHeaders(tabLabel as SUBSCRIPTION_CONSTANTS.TAB_ENUM));
    setCurrentPage(DEFAULT_PAGE);
    setSortOptions(newSortType);
    setShowAppsInSearch(DefaultShowAppsInSearch);
    refetch(
      {
        filterOptions: newFilterOptions,
        searchOptions: {
          first: tabLabel === SUBSCRIPTION_CONSTANTS.TAB_ENUM.UPCOMING ? maxThresholdForResults : rowsPerPage,
          offset: 0,
          sort: newSortType,
        },
      },
      DefaultShowAppsInSearch
    );
  };

  return (
    <div
      className={classNames({
        'tab-container-subscriptions': !isDesignIteration1Enabled,
        'tab-container-subscriptions-with-label': label,
      })}
    >
      {label && !isDesignIteration1Enabled && <TableHeading className="pt-32 ph-28">{label}</TableHeading>}
      {shouldHideTabs ? (
        renderTabTable(SUBSCRIPTION_CONSTANTS.TAB_ENUM.ACTIVE)
      ) : (
        <Tabs testId="subscription-table-tabs" onTabSelect={onTabSelect} selectedTabKey={kebabCase(selectedTab)}>
          <TabPanel label={SUBSCRIPTION_CONSTANTS.TAB_ENUM.ACTIVE}>
            {renderTabTable(SUBSCRIPTION_CONSTANTS.TAB_ENUM.ACTIVE)}
          </TabPanel>
          <TabPanel label={SUBSCRIPTION_CONSTANTS.TAB_ENUM.INACTIVE}>
            {renderTabTable(SUBSCRIPTION_CONSTANTS.TAB_ENUM.INACTIVE)}
          </TabPanel>
          {isFreeTabEnabled ? (
            <TabPanel label={SUBSCRIPTION_CONSTANTS.TAB_ENUM.FREE}>
              {renderTabTable(SUBSCRIPTION_CONSTANTS.TAB_ENUM.FREE)}
            </TabPanel>
          ) : null}
          {isFutureDatedSubscriptionsEnabled ? (
            <TabPanel label={SUBSCRIPTION_CONSTANTS.TAB_ENUM.UPCOMING}>
              {renderTabTable(SUBSCRIPTION_CONSTANTS.TAB_ENUM.UPCOMING)}
            </TabPanel>
          ) : null}
        </Tabs>
      )}
    </div>
  );
};
